// Surface 9's step ribbon (U128, B1009): where you are in the flow, on every panel.
//
// The audit's first finding: six screens in sequence and not one of them carries a step count, a
// map or a back control, so a person part-way through has no idea whether they are near the end.
//
// B1009's lesson, kept as a rule rather than a comment: A DRAWING REPEATED ACROSS TWELVE FRAMES
// IS A DATA STRUCTURE. The drawings were hand-written per frame and a blanket rename left two of
// them reading "Reading · Reading" with the notetaker step gone. So there is ONE ordered list
// here, every panel renders the same list, and a step cannot be renamed on one screen and not
// another.
import type { Journey } from './journey.js';

/** The six, in order. `key` is what a panel names itself; the word is what the person reads. */
export const STEPS = [
  { key: 'in', word: 'Signed in' },
  { key: 'held', word: 'What Cobrain has' },
  { key: 'notetakers', word: 'Your notetakers' },
  { key: 'reading', word: 'Reading' },
  { key: 'voice', word: 'Your voice' },
  { key: 'ready', word: 'Ready' },
] as const;

export type StepKey = (typeof STEPS)[number]['key'];

/**
 * Which panel a journey is standing on. The gate's stages do not map one to one -- 'connect'
 * covers both the mailbox and the notetakers -- so the mailbox's own evidence decides between
 * them, which is the same rule panel 2 draws on (U137).
 */
export function stepOf(journey: Journey, mailStarted: boolean): StepKey {
  switch (journey.stage) {
    case 'intro': return 'in';
    case 'connect': return mailStarted ? 'notetakers' : 'held';
    case 'parked':
    case 'loading': return 'reading';
    case 'drafts': return 'voice';
    case 'areas':
    case 'triage': return 'ready';
    default: return 'held';
  }
}

/**
 * The ribbon. `at` is the step being shown; anything before it is done, anything after is still
 * to come. A step the person genuinely skipped is struck through rather than marked done, so
 * "Cobrain already had your notetaker" and "you finished the notetaker step" do not look alike.
 */
export function Ribbon({ at, skipped = [], onBack }: { at: StepKey; skipped?: StepKey[]; onBack?: () => void }) {
  const here = STEPS.findIndex((s) => s.key === at);
  return (
    <nav className="ribbon" aria-label="Setting up">
      {onBack ? <button className="rb-back" onClick={onBack}>&lsaquo; Back</button> : null}
      <ol className="rb">
        {STEPS.map((s, i) => {
          const state = skipped.includes(s.key) ? 'skip' : i < here ? 'done' : i === here ? 'now' : '';
          return (
            <li key={s.key} className={state ? `rb-i ${state}` : 'rb-i'} aria-current={i === here ? 'step' : undefined}>
              <span className="rb-sr">{i + 1} of {STEPS.length}: </span>{s.word}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
